/**
 * Клиентская часть Web Push (FCM) на странице приложения.
 *
 * Поток:
 * 1. После авторизации — регистрация SW, получение FCM-токена, сохранение на бэкенд.
 * 2. Вкладка открыта — push приходит в onMessage, уведомление показывает страница.
 * 3. Вкладка закрыта/в фоне — уведомление и клик обрабатывает firebase-messaging-sw.js.
 *
 * iOS Safari: системный диалог разрешений только из явного действия пользователя (tap).
 */
import {getMessaging, getToken, isSupported, onMessage} from 'firebase/messaging';
import config from '@appConfig';
import UserApi from 'keysso/api/UserApi';
import {getFirebaseApp, initFirebase} from 'keysso/services/firebase';
import {emitter} from 'keysso/plugins/emitter';
import {Listeners} from 'keysso/constants/listeners';

// npm run build → NODE_ENV=production → .min.js; npm run dev → .js (см. webpack.make.js)
const SERVICE_WORKER_URL = process.env.NODE_ENV === 'production'
    ? '/public/firebase-messaging-sw.min.js'
    : '/public/firebase-messaging-sw.js';
let messagingInitPromise = null;
/** Последний токен, уже отправленный на бэкенд — чтобы не дублировать запросы. */
let registeredToken = null;
let onMessageAttached = false;
/** Регистрация FCM SW — нужна для getToken и showNotification с этой же страницы. */
let serviceWorkerRegistration = null;
/** Защита от двойного показа одного и того же push во foreground. */
let lastShownDedupeKey = null;
let lastShownAt = 0;
const FOREGROUND_DEDUPE_MS = 5000;

const NOTIFICATION_ICON = '/favicon-96x96.png';

/** iPhone, iPad, iPod — WebKit с ограничением user gesture для Notification.requestPermission. */
function isIOSDevice() {
    return /iPad|iPhone|iPod/i.test(navigator.userAgent)
        || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

/** Тип устройства (не ширина viewport): мобильный браузер или десктоп. */
function isMobileDevice() {
    if (navigator.userAgentData?.mobile === true) {
        return true;
    }

    return /Android|iPhone|iPod|Mobile/i.test(navigator.userAgent);
}

/** На iOS диалог разрешений без tap пользователя не показывается. */
function needsUserGestureForPushPermission() {
    return isIOSDevice();
}

function isPushPermissionGranted() {
    return 'Notification' in window && Notification.permission === 'granted';
}

/** Собирает заголовок/текст из notification или data (бэкенд шлёт data-only). */
function buildNotificationContent(payload) {
    const notification = payload.notification ?? {};
    const data = payload.data ?? {};

    return {
        title: notification.title || data.title || 'Keys.so',
        body: notification.body || data.body || '',
        data: data,
        tag: payload.messageId || data.messageId || 'keys-so-fcm',
        icon: window.location.origin + NOTIFICATION_ICON,
    };
}

/**
 * Показывает системное уведомление, пока вкладка открыта.
 * Через registration SW, чтобы клик обрабатывался в firebase-messaging-sw.js.
 */
function getForegroundDedupeKey(payload) {
    const messageId = payload.messageId || payload.data?.messageId;
    if (messageId) {
        return String(messageId);
    }

    const {title, body, tag} = buildNotificationContent(payload);
    return `${tag}:${title}:${body}`;
}

async function showForegroundNotification(payload) {
    if (Notification.permission !== 'granted') {
        console.warn('FCM: Notification.permission !== granted');
        return;
    }

    const dedupeKey = getForegroundDedupeKey(payload);
    const now = Date.now();
    if (dedupeKey === lastShownDedupeKey && now - lastShownAt < FOREGROUND_DEDUPE_MS) {
        return;
    }
    lastShownDedupeKey = dedupeKey;
    lastShownAt = now;

    const {title, body, data, tag, icon} = buildNotificationContent(payload);
    const registration = serviceWorkerRegistration || await navigator.serviceWorker.ready;

    await registration.showNotification(title, {
        body: body,
        data: data,
        tag: tag,
        icon: icon,
    });
}

/** Прямой канал FCM, когда push доставлен в открытую вкладку (без участия SW). */
function attachForegroundMessageHandler(messaging) {
    if (onMessageAttached) {
        return;
    }

    onMessage(messaging, (payload) => {
        console.info('FCM: сообщение в открытой вкладке', payload);
        showForegroundNotification(payload).catch((error) => {
            console.warn('FCM: не удалось показать уведомление', error);
        });
    });
    onMessageAttached = true;
}

function isMessagingConfigured() {
    return Boolean(
        config.FIREBASE_VAPID_KEY
        && config.FIREBASE_API_KEY
        && config.FIREBASE_PROJECT_ID
        && config.FIREBASE_APP_ID,
    );
}

/**
 * Регистрирует отдельный SW только для FCM.
 * Scope /public/ — SW не перехватывает навигацию основного приложения на /.
 */
async function registerServiceWorker() {
    if (!('serviceWorker' in navigator)) {
        return null;
    }

    return navigator.serviceWorker.register(SERVICE_WORKER_URL, {
        scope: '/public/',
    });
}

async function requestNotificationPermission() {
    if (!('Notification' in window)) {
        return false;
    }

    if (Notification.permission === 'granted') {
        return true;
    }

    if (Notification.permission === 'denied') {
        return false;
    }

    const permission = await Notification.requestPermission();
    return permission === 'granted';
}

/**
 * @param {{ requestPermission?: boolean }} options
 * requestPermission — вызывать Notification.requestPermission (нужен user gesture на iOS).
 */
async function obtainAndSaveToken(options = {}) {
    const requestPermission = options.requestPermission ?? true;

    if (!isMessagingConfigured()) {
        console.warn('FCM: не заданы FIREBASE_VAPID_KEY или другие переменные Firebase в .env (нужна пересборка webpack)');
        return null;
    }

    // На iOS requestPermission() должен быть первым await в цепочке после user gesture.
    if (requestPermission) {
        if (!(await requestNotificationPermission())) {
            console.warn('FCM: нет разрешения на уведомления (Notification.permission =', Notification.permission, ')');
            return null;
        }
    } else if (!isPushPermissionGranted()) {
        return null;
    }

    if (!(await isSupported())) {
        if (!window.isSecureContext) {
            console.warn(
                'FCM: нужен secure context.\n'
                + 'Для локальной разработки можно добавить в исключение chrome://flags/#unsafely-treat-insecure-origin-as-secure.'
            );
        } else {
            console.warn('FCM: браузер не поддерживает Firebase Messaging (IndexedDB, Service Worker или Push API)');
        }
        return null;
    }

    await initFirebase();
    const app = getFirebaseApp();
    if (!app) {
        console.warn('FCM: Firebase App не инициализирован');
        return null;
    }

    let registration;
    try {
        registration = await registerServiceWorker();
    } catch (error) {
        console.warn('FCM: не удалось зарегистрировать service worker', SERVICE_WORKER_URL, error);
        return null;
    }

    if (!registration) {
        console.warn('FCM: service worker недоступен в этом браузере');
        return null;
    }

    serviceWorkerRegistration = registration;

    const messaging = getMessaging(app);
    attachForegroundMessageHandler(messaging);

    // После обновления SW переподписываемся на push, иначе onMessage может не сработать.
    try {
        await registration.update();
    } catch (error) {
        console.warn('FCM: не удалось обновить service worker', error);
    }

    let token;
    try {
        token = await getToken(messaging, {
            vapidKey: config.FIREBASE_VAPID_KEY,
            serviceWorkerRegistration: registration,
        });
    } catch (error) {
        console.warn('FCM: не удалось получить токен от Firebase', error);
        return null;
    }

    if (!token) {
        return null;
    }

    if (token === registeredToken) {
        return token;
    }

    try {
        await UserApi.saveFcmToken({
            token: token,
            userAgent: navigator.userAgent,
            isMobile: isMobileDevice(),
        });
    } catch (error) {
        console.warn('FCM: API /users/fcm-token вернул ошибку', error);
        return token;
    }
    registeredToken = token;

    console.info('FCM: токен зарегистрирован');

    return token;
}

function resetMessagingInitState() {
    messagingInitPromise = null;
}

/**
 * @param {{ userInitiated?: boolean }} options
 * userInitiated — вызов из обработчика клика (разрешён запрос Notification на iOS).
 */
function initFirebaseMessaging(options = {}) {
    const userInitiated = options.userInitiated === true;

    if (!userInitiated && needsUserGestureForPushPermission() && !isPushPermissionGranted()) {
        return Promise.resolve(null);
    }

    if (userInitiated) {
        resetMessagingInitState();
    }

    if (messagingInitPromise) {
        return messagingInitPromise;
    }

    const requestPermission = userInitiated
        || (Notification.permission === 'default' && !needsUserGestureForPushPermission());

    messagingInitPromise = obtainAndSaveToken({
        requestPermission: requestPermission,
    }).catch((error) => {
        resetMessagingInitState();
        console.warn('FCM init failed', error);
        return null;
    });

    return messagingInitPromise;
}

/**
 * Включение push из обработчика клика (переключатель в панели; на iOS — единственный способ запросить разрешение).
 * Вызывать синхронно из click/tap, без await до возврата promise.
 *
 * @returns {Promise<boolean>} true — разрешение получено и токен сохранён
 */
function enablePushNotifications() {
    resetMessagingInitState();

    return initFirebaseMessaging({
        userInitiated: true,
    }).then((token) => Boolean(token));
}

// Автоподписка после загрузки данных авторизованного пользователя.
emitter.on(Listeners.GLOBAL_STORE_GET_USER, (user) => {
    if (user?.authState > 0) {
        initFirebaseMessaging();
    }
});

export {
    initFirebaseMessaging,
    requestNotificationPermission,
    enablePushNotifications,
};
